import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, MapPin } from 'lucide-react';

const PrivacyPolicy = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Hero header */}
      <div className="bg-[#111827] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-8 pt-8 pb-16">
          <div className="flex items-center justify-between mb-12">
            <Link to="/">
              <span className="text-lg font-black tracking-tighter uppercase text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Trademark<span className="text-[#FF1010]">.</span>Agency
              </span>
            </Link>
            <Link to="/" className="flex items-center gap-2 text-sm text-white/60 hover:text-[#FF1010] transition-colors font-medium">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-widest text-[#FF1010] block mb-3">Legal</span>
            <h1 className="text-4xl sm:text-5xl font-black text-white uppercase tracking-tighter leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Privacy Policy
            </h1>
            <p className="text-sm text-white/40 mt-3">Last Updated: February 2026</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 md:px-8 py-16">
        <div className="space-y-10">
          <section>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 bg-[#FF1010] flex items-center justify-center text-white font-black text-xs" style={{ fontFamily: "'Outfit', sans-serif" }}>01</span>
              <h2 className="text-base font-black text-[#111827] uppercase tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Information We Collect</h2>
            </div>
            <div className="pl-11 text-sm text-[#4B5563] leading-relaxed space-y-3">
              <p>Trademark Agency USA collects personal information that you provide directly when using our services or contacting us. This includes:</p>
              <ul className="list-none space-y-2">
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#FF1010] mt-1.5 flex-shrink-0" /><span>Full name, email address, and phone number</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#FF1010] mt-1.5 flex-shrink-0" /><span>Details about your trademark registration needs</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#FF1010] mt-1.5 flex-shrink-0" /><span>Usage data collected through cookies and similar technologies</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#FF1010] mt-1.5 flex-shrink-0" /><span>Device information, browser type, and IP address</span></li>
              </ul>
            </div>
          </section>

          <div className="h-px bg-[#E5E7EB]" />

          <section>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 bg-[#FF1010] flex items-center justify-center text-white font-black text-xs" style={{ fontFamily: "'Outfit', sans-serif" }}>02</span>
              <h2 className="text-base font-black text-[#111827] uppercase tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>How We Use Your Information</h2>
            </div>
            <div className="pl-11 text-sm text-[#4B5563] leading-relaxed space-y-3">
              <p>We use the information we collect to:</p>
              <ul className="list-none space-y-2">
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#FF1010] mt-1.5 flex-shrink-0" /><span>Provide and manage our trademark registration services</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#FF1010] mt-1.5 flex-shrink-0" /><span>Communicate with you about your case and send status updates</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#FF1010] mt-1.5 flex-shrink-0" /><span>Improve our website, services, and user experience</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#FF1010] mt-1.5 flex-shrink-0" /><span>Comply with legal obligations and regulatory requirements</span></li>
              </ul>
            </div>
          </section>

          <div className="h-px bg-[#E5E7EB]" />

          <section>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 bg-[#FF1010] flex items-center justify-center text-white font-black text-xs" style={{ fontFamily: "'Outfit', sans-serif" }}>03</span>
              <h2 className="text-base font-black text-[#111827] uppercase tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Information Sharing</h2>
            </div>
            <p className="pl-11 text-sm text-[#4B5563] leading-relaxed">We do not sell your personal information to third parties. We may share information with trusted service providers who assist in operating our website and delivering our services, or when required by law. All third-party providers are bound by confidentiality agreements.</p>
          </section>

          <div className="h-px bg-[#E5E7EB]" />

          <section>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 bg-[#FF1010] flex items-center justify-center text-white font-black text-xs" style={{ fontFamily: "'Outfit', sans-serif" }}>04</span>
              <h2 className="text-base font-black text-[#111827] uppercase tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Data Security</h2>
            </div>
            <p className="pl-11 text-sm text-[#4B5563] leading-relaxed">We implement appropriate technical and organizational security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. This includes encryption, secure servers, and regular security audits.</p>
          </section>

          <div className="h-px bg-[#E5E7EB]" />

          <section>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 bg-[#FF1010] flex items-center justify-center text-white font-black text-xs" style={{ fontFamily: "'Outfit', sans-serif" }}>05</span>
              <h2 className="text-base font-black text-[#111827] uppercase tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Your Rights</h2>
            </div>
            <div className="pl-11 text-sm text-[#4B5563] leading-relaxed space-y-3">
              <p>You have the right to:</p>
              <ul className="list-none space-y-2">
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#FF1010] mt-1.5 flex-shrink-0" /><span>Access, correct, or delete your personal information</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#FF1010] mt-1.5 flex-shrink-0" /><span>Opt out of marketing communications at any time</span></li>
                <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#FF1010] mt-1.5 flex-shrink-0" /><span>Request a copy of the data we hold about you</span></li>
              </ul>
              <p>To exercise these rights, contact us at <a href="mailto:info@trademarkagencyusa.com" className="text-[#FF1010] font-semibold hover:underline">info@trademarkagencyusa.com</a>.</p>
            </div>
          </section>

          <div className="h-px bg-[#E5E7EB]" />

          <section>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 bg-[#FF1010] flex items-center justify-center text-white font-black text-xs" style={{ fontFamily: "'Outfit', sans-serif" }}>06</span>
              <h2 className="text-base font-black text-[#111827] uppercase tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Cookies</h2>
            </div>
            <p className="pl-11 text-sm text-[#4B5563] leading-relaxed">Our website uses cookies to enhance your browsing experience and analyze site traffic. You can control cookie preferences through your browser settings. Some cookies are essential for the website to function properly and cannot be disabled.</p>
          </section>

          <div className="h-px bg-[#E5E7EB]" />

          <section>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 bg-[#FF1010] flex items-center justify-center text-white font-black text-xs" style={{ fontFamily: "'Outfit', sans-serif" }}>07</span>
              <h2 className="text-base font-black text-[#111827] uppercase tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Contact Us</h2>
            </div>
            <div className="pl-11 space-y-3">
              <p className="text-sm text-[#4B5563] leading-relaxed">For any questions about this Privacy Policy, reach out to us:</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="mailto:info@trademarkagencyusa.com" className="flex items-center gap-2 text-sm text-[#111827] font-semibold hover:text-[#FF1010] transition-colors">
                  <Mail className="w-4 h-4 text-[#FF1010]" /> info@trademarkagencyusa.com
                </a>
                <a href="tel:+18001234567" className="flex items-center gap-2 text-sm text-[#111827] font-semibold hover:text-[#FF1010] transition-colors">
                  <Phone className="w-4 h-4 text-[#FF1010]" /> +1 (800) 123-4567
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#4B5563]">
                <MapPin className="w-4 h-4 text-[#FF1010]" /> 265 E River Park Cir Ste 110, Fresno, CA 93720
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-5 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-xs text-[#9CA3AF]">&copy; {new Date().getFullYear()} Trademark Agency USA. All rights reserved.</p>
          <div className="flex gap-6 text-xs">
            <Link to="/privacy-policy" className="text-[#FF1010] font-bold">Privacy Policy</Link>
            <Link to="/terms-conditions" className="text-[#9CA3AF] hover:text-[#FF1010] transition-colors font-medium">Terms & Conditions</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
